/* eslint-disable prefer-destructuring */
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card, CardBody, Col,
} from 'reactstrap';
import debounce from 'lodash.debounce';
import ReactTableBase from '@/shared/components/table/ReactTableBase';
import PaginationContainer from './PaginationContainer';

const DataReactTable = ({
  reactTableData, 
  page, 
  setPage, 
  totalRecords, 
  loading,
  handleFiltersUpdate,
  filterValues,
  handleSort,
  sortedColumn,
}) => {
  // rows are now managed by the parent (filtered & sorted client-side),
  // so we read directly from the prop rather than holding stale local state.
  const [filters, setFilters] = useState({});

  const [isEditable] = useState(false);
  const [isResizable] = useState(false);
  const [isSortable] = useState(true);
  const [withDragAndDrop] = useState(false);
  const [withPagination] = useState(false);
  const [withSearchEngine] = useState(false);

  const delay = 500;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFilterUpdate = useCallback(debounce(handleFiltersUpdate, delay), [handleFiltersUpdate, delay]);

  const onFiltersUpdate = (key, value, filterType) => {
    const newFilters = { ...filters };
    newFilters[key] = { value, filterType };
    setFilters(newFilters);

    debouncedFilterUpdate(key, value, filterType);
  };

  const pageSize = 20;

  const paginatedRows = reactTableData.tableRowsData
  ? reactTableData.tableRowsData.slice((page - 1) * pageSize, page * pageSize)
  : [];

  const tableConfig = {
    isEditable,
    isResizable,
    isSortable,
    withDragAndDrop,
    withPagination,
    withSearchEngine,
    manualPageSize: [10, 20, 30, 40],
    placeholder: 'Search by First name...',
  };

  return (
    <Col md={12} lg={12}>
      <Card>
        <CardBody>
          <ReactTableBase
            key={withSearchEngine || isResizable || isEditable ? 'modified' : 'common'}
            columns={reactTableData.tableHeaderData}
            data={paginatedRows}
            tableConfig={tableConfig}
            loading={loading}
            filters={filters}
            handleFiltersUpdate={onFiltersUpdate}
            filterValues={filterValues}
            handleSort={handleSort}
            sortedColumn={sortedColumn}
          />
          {!loading && <PaginationContainer page={page} setPage={setPage} totalRecords={totalRecords} />}
        </CardBody>
      </Card>
    </Col>
  );
};

DataReactTable.propTypes = {
  reactTableData: PropTypes.shape({
    tableHeaderData: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
    })),
    tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
    defaultTableHeaderData: PropTypes.arrayOf(PropTypes.shape()),
    defaultTableRowData: PropTypes.arrayOf(PropTypes.shape()),
  }).isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  totalRecords: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  handleFiltersUpdate: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  filterValues: PropTypes.object.isRequired,
  handleSort: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  sortedColumn: PropTypes.object.isRequired,
};

export default DataReactTable;
